class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.setupTermsGate();
  }

  // Terms & Conditions gate: "Check out" stays disabled until the box is
  // ticked. The acceptance is remembered for the browsing session under the
  // same key the BPH Logistics delivery block reads on the cart page, so the
  // cart page's own terms box is pre-ticked and the delivery details modal
  // launches straight away from the /cart#delivery-details link.
  static get TERMS_ACCEPTED_KEY() {
    return 'bph:terms-accepted';
  }

  setupTermsGate() {
    this.termsCheckbox = this.querySelector('#cart-notification-terms');
    this.checkoutLink = this.querySelector('#cart-notification-checkout');
    if (!this.termsCheckbox || !this.checkoutLink) return;

    this.termsCheckbox.checked = this.isTermsAccepted();
    this.updateCheckoutState();

    this.termsCheckbox.addEventListener('change', () => {
      this.persistTermsAcceptance(this.termsCheckbox.checked);
      this.updateCheckoutState();
    });

    this.checkoutLink.addEventListener('click', (evt) => {
      if (this.termsCheckbox.checked) return;
      evt.preventDefault();
      this.termsCheckbox.focus();
      this.termsCheckbox.closest('.cart-notification__terms')?.classList.add('cart-notification__terms--attention');
    });
  }

  isTermsAccepted() {
    try {
      return !!sessionStorage.getItem(CartNotification.TERMS_ACCEPTED_KEY);
    } catch (_) {
      return false;
    }
  }

  persistTermsAcceptance(accepted) {
    try {
      if (accepted) sessionStorage.setItem(CartNotification.TERMS_ACCEPTED_KEY, new Date().toISOString());
      else sessionStorage.removeItem(CartNotification.TERMS_ACCEPTED_KEY);
    } catch (_) {
      /* storage unavailable — the gate still works for this popup */
    }
  }

  updateCheckoutState() {
    const accepted = this.termsCheckbox.checked;
    this.checkoutLink.setAttribute('aria-disabled', accepted ? 'false' : 'true');
    if (accepted) {
      this.checkoutLink.removeAttribute('tabindex');
      this.termsCheckbox.closest('.cart-notification__terms')?.classList.remove('cart-notification__terms--attention');
    } else {
      this.checkoutLink.setAttribute('tabindex', '-1');
    }
  }

  open() {
    // Re-sync with an acceptance recorded elsewhere this session (cart page)
    if (this.termsCheckbox && !this.termsCheckbox.checked && this.isTermsAccepted()) {
      this.termsCheckbox.checked = true;
      this.updateCheckoutState();
    }

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
